import { BaseService, ServiceError, ValidationSchema } from './BaseService';
import { 
  knowledgeArticleRepository, 
  KnowledgeArticle 
} from '../repositories/KnowledgeArticleRepository';

/**
 * Input type for creating a knowledge article
 */
export interface CreateKnowledgeArticleInput {
  company_id: string;
  category: string;
  title: string;
  content: string;
  tags?: string[];
}

/**
 * Input type for updating a knowledge article
 */
export interface UpdateKnowledgeArticleInput {
  category?: string;
  title?: string;
  content?: string;
  tags?: string[];
}

/**
 * Service for managing knowledge base articles
 * Handles business logic and validation for agent knowledge library
 */
export class KnowledgeArticleService extends BaseService {
  /**
   * Validation schema for creating a knowledge article
   */
  private createSchema: ValidationSchema = {
    company_id: {
      required: true,
      type: 'string',
      message: 'Company ID is required'
    },
    category: {
      required: true,
      type: 'string',
      min: 2,
      max: 100,
      message: 'Category must be between 2 and 100 characters'
    },
    title: {
      required: true,
      type: 'string',
      min: 3,
      max: 200,
      message: 'Title must be between 3 and 200 characters'
    },
    content: {
      required: true,
      type: 'string',
      min: 10,
      message: 'Content must be at least 10 characters'
    }
  };

  /**
   * Get all articles for a company
   */
  async getArticles(companyId: string): Promise<KnowledgeArticle[]> {
    try {
      return await knowledgeArticleRepository.findByCompany(companyId);
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Get articles by category
   */
  async getArticlesByCategory(
    companyId: string,
    category: string
  ): Promise<KnowledgeArticle[]> {
    try {
      return await knowledgeArticleRepository.findByCategory(companyId, category);
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Search articles
   */
  async searchArticles(
    companyId: string,
    searchTerm: string
  ): Promise<KnowledgeArticle[]> {
    try {
      if (!searchTerm || searchTerm.trim().length < 2) {
        throw ServiceError.validation([{
          field: 'searchTerm',
          message: 'Search term must be at least 2 characters'
        }]);
      }

      return await knowledgeArticleRepository.search(companyId, searchTerm.trim());
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Get most viewed articles
   */
  async getMostViewed(companyId: string, limit: number = 10): Promise<KnowledgeArticle[]> {
    try {
      return await knowledgeArticleRepository.getMostViewed(companyId, limit);
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Get all categories for a company
   */
  async getCategories(companyId: string): Promise<string[]> {
    try {
      return await knowledgeArticleRepository.getCategories(companyId);
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Get articles by tag
   */
  async getArticlesByTag(companyId: string, tag: string): Promise<KnowledgeArticle[]> {
    try {
      return await knowledgeArticleRepository.findByTag(companyId, tag);
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Get a single article by ID
   */
  async getArticle(id: string): Promise<KnowledgeArticle> {
    try {
      const article = await knowledgeArticleRepository.findById(id);
      if (!article) {
        throw ServiceError.notFound('Knowledge article', id);
      }
      return article;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Create a new knowledge article
   */
  async createArticle(input: CreateKnowledgeArticleInput): Promise<KnowledgeArticle> {
    try {
      // Validate input
      this.validate(input, this.createSchema);

      // Sanitize data
      const sanitized = {
        company_id: input.company_id,
        category: this.sanitizeString(input.category) || '',
        title: this.sanitizeString(input.title) || '',
        content: this.sanitizeString(input.content) || '',
        tags: input.tags ? input.tags.map(tag => this.sanitizeString(tag) || '').filter(t => t) : [],
        view_count: 0
      };

      // Create article
      const article = await knowledgeArticleRepository.create(sanitized);

      // Emit event
      await this.emit('article.created', { article });

      return article;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Update an existing knowledge article
   */
  async updateArticle(id: string, input: UpdateKnowledgeArticleInput): Promise<KnowledgeArticle> {
    try {
      // Check if article exists
      const existing = await knowledgeArticleRepository.findById(id);
      if (!existing) {
        throw ServiceError.notFound('Knowledge article', id);
      }

      // Sanitize data
      const sanitized: Partial<KnowledgeArticle> = {};
      if (input.category) sanitized.category = this.sanitizeString(input.category) || undefined;
      if (input.title) sanitized.title = this.sanitizeString(input.title) || undefined;
      if (input.content) sanitized.content = this.sanitizeString(input.content) || undefined;
      if (input.tags) {
        sanitized.tags = input.tags.map(tag => this.sanitizeString(tag) || '').filter(t => t);
      }

      // Update article
      const updated = await knowledgeArticleRepository.update(id, sanitized);

      // Emit event
      await this.emit('article.updated', { article: updated });

      return updated;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Record article view (increments view count)
   */
  async recordView(id: string): Promise<KnowledgeArticle> {
    try {
      const article = await knowledgeArticleRepository.incrementViews(id);
      await this.emit('article.viewed', { article });
      return article;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Delete a knowledge article
   */
  async deleteArticle(id: string): Promise<void> {
    try {
      // Check if article exists
      const article = await knowledgeArticleRepository.findById(id);
      if (!article) {
        throw ServiceError.notFound('Knowledge article', id);
      }

      // Delete article
      await knowledgeArticleRepository.delete(id);

      // Emit event
      await this.emit('article.deleted', { articleId: id });
    } catch (error) {
      this.handleError(error);
    }
  }
}

// Export singleton instance
export const knowledgeArticleService = new KnowledgeArticleService();
